import {
  TaskPermissions,
  getDefaultTaskPermissions,
  combineTaskPermissions,
  isCorrectTaskPermissions,
  nothingAllowedTaskPermissions
} from './task-permissions'

export interface ColumnPermissions {
  editTitle: boolean
  delete: boolean
  move: 'no' | 'project' | 'yes'
  addTask: boolean

  allTasks: TaskPermissions
  withMeTasks?: TaskPermissions // user is added to task notifications
  myTasks?: TaskPermissions // assigned to user (by adding User sticker)
  createdByMeTasks?: TaskPermissions
}

export type TaskPermissionsType = 'withMeTasks' | 'myTasks' | 'createdByMeTasks'

export const getDefaultColumnPermissions = (): ColumnPermissions => {
  return {
    editTitle: true,
    delete: true,
    move: 'yes',
    addTask: true,
    allTasks: getDefaultTaskPermissions(),
    withMeTasks: undefined,
    myTasks: undefined,
    createdByMeTasks: undefined
  }
}

export const allowAllOnColumn = (
  perm: ColumnPermissions
): ColumnPermissions => {
  return {
    ...perm,
    editTitle: true,
    delete: true,
    move: 'yes',
    addTask: true
  }
}

export const disallowAllOnColumn = (
  perm: ColumnPermissions
): ColumnPermissions => {
  return {
    ...perm,
    editTitle: false,
    delete: false,
    move: 'no',
    addTask: false
  }
}

export const nothingAllowedColumnPermissions = (): ColumnPermissions => ({
  editTitle: false,
  delete: false,
  move: 'no',
  addTask: false,
  allTasks: nothingAllowedTaskPermissions()
})

export const isCorrectColumnPermissions = (p: ColumnPermissions): boolean => {
  return (
    !!p &&
    !!p.move &&
    isCorrectTaskPermissions(p.allTasks) &&
    (!p.withMeTasks || isCorrectTaskPermissions(p.withMeTasks)) &&
    (!p.myTasks || isCorrectTaskPermissions(p.myTasks)) &&
    (!p.createdByMeTasks || isCorrectTaskPermissions(p.createdByMeTasks))
  )
}

export const combineColumnPermissions = (
  a: ColumnPermissions,
  b: ColumnPermissions
): ColumnPermissions => {
  if (!a) {
    return b
  }
  if (!b) {
    return a
  }
  const moveOrder: Array<ColumnPermissions['move']> = ['no', 'project', 'yes']
  const moveIx = Math.max(
    0,
    moveOrder.indexOf(a.move),
    moveOrder.indexOf(b.move)
  )
  return {
    editTitle: a.editTitle || b.editTitle,
    delete: a.delete || b.delete,
    move: moveOrder[moveIx],
    addTask: a.addTask || b.addTask,
    allTasks: combineTaskPermissions(a.allTasks, b.allTasks) ?? a.allTasks,
    withMeTasks: combineTaskPermissions(a.withMeTasks, b.withMeTasks),
    myTasks: combineTaskPermissions(a.myTasks, b.myTasks),
    createdByMeTasks: combineTaskPermissions(
      a.createdByMeTasks,
      b.createdByMeTasks
    )
  }
}
